import { PersonsV3 } from '@frontend/api-person';
import { SchedulerV3 } from '@frontend/api-schedule-v3';
import { AdaptoActions } from '@frontend/adapto-actions';
import { Actions } from '@frontend/contact-actions';
import { useDevToolsStore } from '@frontend/devtools';
import { PaymentsAppointmentPromoWrapper } from '@frontend/high-value-adoption';
import { useTranslation } from '@frontend/i18n';
import { REQUEST_ORIGINS } from '@frontend/person-invoice-create';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { SpinningLoader } from '@frontend/design-system';
import { useScheduleActionsContainerMethod } from '../../../context/ScheduleActionsContainerContext';
import { EditAppointmentActionButton } from './edit-appointment-action-button';
import { sendAutomatedReminder } from './send-reminder';

const CACHE_TIME = 5 * 60 * 1000; // 5 minutes
const TRACKING_ID_SUFFIX = 'appointment-contact-actions';

type ActionMenuButtonsProps = {
  personId: string;
  locationId: string;
  appointmentId?: string;
  closeModal?: () => void;
};

export const AppointmentEventActionButtons = ({
  personId,
  locationId,
  appointmentId,
  closeModal,
}: ActionMenuButtonsProps) => {
  const { t } = useTranslation('schedule');
  const { closeModal: closeScheduleActionsModal } = useScheduleActionsContainerMethod();

  const isSmall = useMatchMedia({ maxWidth: breakpoints.xsmall.max });

  const { hasWSM } = SchedulerV3.Helpers.useCheckHasWSM(locationId);

  const {
    options: { isDebugModeOn },
  } = useDevToolsStore();

  const { data: personData, isLoading: isLoadingPersonData } = PersonsV3.PersonQueries.useGetPersonLegacyQuery(
    {
      personId,
      locationIds: [locationId],
    },
    {
      enabled: !!personId,
      cacheTime: CACHE_TIME,
      staleTime: CACHE_TIME,
      select: (data) => PersonsV3.PersonHelpers.convertPersonV3ToPerson(data),
    }
  );

  if (isLoadingPersonData) {
    return <SpinningLoader size='xs' />;
  }

  const sendFormAction = personData ? (
    <Actions.SendForm
      key='send-form-action'
      label={t('Send Form')}
      context={{ person: personData, locationId }}
      trackingIdSuffix={TRACKING_ID_SUFFIX}
    />
  ) : null;
  const messageAction = personData ? (
    <Actions.Message
      key='message-action'
      label={t('Send Message')}
      context={{ personId: personData.PersonID, locationId, onSuccess: closeModal }}
      trackingIdSuffix={TRACKING_ID_SUFFIX}
    />
  ) : null;
  const callAction = personData ? (
    <Actions.Call
      key='call-action'
      label={t('Click to Call')}
      context={{ personId: personData.PersonID, locationId, person: personData }}
      trackingIdSuffix={TRACKING_ID_SUFFIX}
      showPopover={false}
    />
  ) : null;

  const paymentAction = personData ? (
    <PaymentsAppointmentPromoWrapper
      key='payment-action'
      locationId={locationId}
      onSettingsOpen={closeScheduleActionsModal}
    >
      <Actions.PaymentsHighlighted
        label={t('Request Payment')}
        context={{ person: personData, personId: personData.PersonID, locationId: locationId }}
        trackingIdSuffix={`promo-${TRACKING_ID_SUFFIX}`}
        origin={REQUEST_ORIGINS.SCHEDULE_CARD}
      />
    </PaymentsAppointmentPromoWrapper>
  ) : null;
  const reviewAction = personData ? (
    <Actions.Review
      key='review-action'
      label={t('Request Review')}
      groupId={locationId}
      personId={personData.PersonID}
      trackingIdSuffix={TRACKING_ID_SUFFIX}
    />
  ) : null;
  const editAction = hasWSM ? <EditAppointmentActionButton key='edit-action' /> : null;
  // This is only available in debug mode so that we can test out sending automated reminder messages
  const sendAutomatedReminderAction =
    personData?.MobilePhone && personData.PersonID && appointmentId && isDebugModeOn ? (
      <AdaptoActions.Action
        key='send-automated-reminder-action'
        label='Send Reminder'
        trackingId={`send-automated-reminder-${TRACKING_ID_SUFFIX}`}
        icon='message-outgoing'
        hoverLabel={'Send an SMS reminder message to the patient'}
        onClick={() => {
          sendAutomatedReminder(personData?.MobilePhone, locationId, personData?.PersonID, appointmentId);
        }}
      />
    ) : null;

  const primaryActions = [messageAction, sendFormAction, paymentAction].filter(Boolean);
  const secondaryActions = [editAction, callAction, reviewAction, sendAutomatedReminderAction].filter(Boolean);

  return (
    <AdaptoActions
      css={{ marginRight: theme.spacing(1), gap: 2, alignItems: 'center' }}
      iconColor='subdued'
      maxActions={isSmall ? 1 : primaryActions.length + 1}
    >
      {primaryActions}
      {secondaryActions}
    </AdaptoActions>
  );
};
